import {
  type PvpTeamUiState,
  teamArenaIsOpen,
  teamControlsEnabled,
  teamHoldsHunt,
} from './team-machine';

/**
 * What the shell shows while a `team_3v3` is on screen, derived once.
 *
 * The duel's `pvpPresentation` answers the same questions for the other mode and
 * the two are deliberately separate functions over separate unions: a single one
 * would take a state that could be either and would need a branch per flag, and
 * a flag that forgot its branch is a HUD over an arena or a lobby over a fight.
 *
 * What they must AGREE on is the shape, because the App reads one object and
 * does not know which mode produced it. That agreement is structural - both
 * produce a `PvpPresentation` - and asserted by a test that walks every state of
 * both machines.
 *
 * Everything here answers "what is on screen", never "what is true": whether the
 * hunt is PAUSED is the coordinator's business and whether the arena is OPEN is
 * the controller's.
 */

import type { PvpPresentation } from './selectors';

export function pvpTeamPresentation(state: PvpTeamUiState): PvpPresentation {
  const dedicated = teamHoldsHunt(state);
  const arena = teamArenaIsOpen(state);

  return {
    panelOpen: state.kind !== 'closed',
    // The team lobby has no discovery of its own: it is reached from the duel
    // machine's, which is where `restoring` lives.
    restoring: false,
    dedicatedMode: dedicated,
    /*
     * The arena's HUD follows the ARENA, not the dedicated mode. `entering_pvp`
     * and `recovering_hunt` are dedicated and have no arena - one is putting it
     * up and the other has already taken it down.
     */
    pvpHudVisible: arena,
    huntHudVisible: !dedicated,
    shellNavigationVisible: !dedicated,
    pvpButtonEnabled: state.kind === 'closed',
    /*
     * A way out from everywhere except the moments that are already on their way
     * out, or mid-transaction. `entering_pvp` is a synchronous sequence with a
     * rollback; `leaving` is already going; and `recovering_hunt` IS the exit.
     */
    canClose:
      state.kind !== 'closed' &&
      state.kind !== 'entering_pvp' &&
      state.kind !== 'leaving' &&
      state.kind !== 'recovering_hunt',
    abandonPending: state.kind === 'running' && state.abandon === 'sending',
    abandonFailed: state.kind === 'running' && state.abandon === 'failed',
    // Only in `ready`, and only until this generation has declared.
    canReady: state.kind === 'ready' && !state.declared,
    controlsEnabled: teamControlsEnabled(state),
    showsRecovery: state.kind === 'recovering_hunt',
    showsResult: state.kind === 'completed',
    showsReconnecting: state.kind === 'reconnecting',
    showsError: state.kind === 'error',
  };
}
